/* global Word console */
import { LIST_BULLET_STYLES } from "../consts";
import { parseListStartNumber, pruneListState, setupNumberedList, tryApplyListStyle } from "../elements/list";
import { addRunsToParagraph } from "../elements/runs";
import { DocumentContent, DocumentElement, ParseState } from "../types";

export async function handleAddElement(
  startElement: Word.Paragraph,
  doc: Word.Document,
  element: DocumentElement,
  state: ParseState,
  after: boolean = false,
  allElementsInDocument: Word.Paragraph[] = [],
  context?: Word.RequestContext
): Promise<Word.Paragraph> {
  let newParagraph: Word.Paragraph | null = null;
  let isList = false;
  let listSetupError = false;

  switch (element.type) {
    case "heading": {
      newParagraph = startElement.insertParagraph("", after ? Word.InsertLocation.after : Word.InsertLocation.before);
      const headingStyles = [
        "Normal",
        "Heading1",
        "Heading2",
        "Heading3",
        "Heading4",
        "Heading5",
        "Heading6",
        "Heading7",
        "Heading8",
        "Heading9",
      ];
      const styleIndex = element.heading_level || 0;
      newParagraph.styleBuiltIn = (
        styleIndex >= 1 && styleIndex <= 9 ? headingStyles[styleIndex] : "Normal"
      ) as Word.BuiltInStyleName;
      state.listState = {};
      break;
    }
    case "paragraph":
      newParagraph = startElement.insertParagraph("", after ? Word.InsertLocation.after : Word.InsertLocation.before);
      newParagraph.styleBuiltIn = "Normal";
      // Detach from any list Word may have auto-attached to
      try {
        newParagraph.detachFromList();
        if (context) await context.sync();
      } catch {
        /* Expected if not in a list */
      }
      break;
    case "list item": {
      newParagraph = startElement.insertParagraph("", after ? Word.InsertLocation.after : Word.InsertLocation.before);

      const tabsLevel = element.tabs ?? 0;
      pruneListState(state, tabsLevel);

      const previousState = state.listState[tabsLevel];
      if (element.list_type === "bullet") {
        try {
          const bulletStyleApplied = await tryApplyListStyle(newParagraph, LIST_BULLET_STYLES, context);
          if (!bulletStyleApplied) {
            newParagraph.styleBuiltIn = "ListParagraph";
            if (context) {
              await context.sync();
            }
          }

          isList = true;
          state.listState[tabsLevel] = {
            listType: element.list_type,
            lastNumber: undefined,
          };
        } catch (e) {
          console.warn("Could not set bullet list style, falling back to text prefix:", e);
          listSetupError = true;
        }
      } else {
        // Numbered list - use prefix number from backend
        const normalizedListType = "number";
        const prefixNumber = parseListStartNumber(element.prefix, normalizedListType);
        
        // Use the prefix number if available, otherwise increment from previous or start at 1
        const startingNumber = prefixNumber ?? (previousState?.lastNumber ?? 0) + 1;

        try {
          const result = await setupNumberedList(newParagraph, tabsLevel, startingNumber, context);

          if (result.success) {
            isList = true;
            state.listState[tabsLevel] = {
              listType: normalizedListType,
              lastNumber: startingNumber,
              listId: result.listId,
            };
          } else {
            listSetupError = true;
          }
        } catch {
          listSetupError = true;
        }
      }

      // If list setup failed, reset to Normal style
      if (listSetupError) {
        try {
          newParagraph.styleBuiltIn = "Normal";
          if (context) {
            await context.sync();
          }
        } catch (resetError) {
          console.warn("Could not reset to Normal style:", resetError);
        }
        isList = false;
      }
      break;
    }
    default:
      break;
  }

  if (!newParagraph) return startElement;

  // For non-list items or if list formatting failed, add prefix as text
  if (element.prefix && !isList) {
    // Clean up the prefix - trim all trailing whitespace (tabs, spaces) and add exactly one space
    const cleanPrefix = element.prefix.trimEnd();
    if (cleanPrefix) {
      element.content.unshift({ content: cleanPrefix + " ", format: [], level: 0 });
    }
  }

  newParagraph.leftIndent = 0;
  newParagraph.firstLineIndent = 0;

  // Set indentation based on element type
  if (isList) {
    const baseListIndent = 18; // 0.25 inches - indent from heading
    const tabsIndent = (element.tabs ?? 0) * 18; // 0.25 inches per tab level
    newParagraph.leftIndent = baseListIndent + tabsIndent;
  } else {
    // For headings and paragraphs, only use tabs-based indent
    newParagraph.leftIndent = (element.tabs ?? 0) * 0.25 * 72;
  }

  await addRunsToParagraph(newParagraph, element.content as (DocumentElement | DocumentContent)[], doc, state, context);

  state.createdElements.push({
    paragraph: newParagraph,
    originalIndex: allElementsInDocument.indexOf(startElement) + (after ? 1 : 0),
  });

  return newParagraph;
}
